import UAParser from 'ua-parser-js'
import { compareVersion } from '../utils/versionUtils'
import { isServer } from '../utils/isServer'

const ANDROID_CLIENT_ID = 'android'
const IOS_CLIENT_ID = 'ios'
const WEB_CLIENT_ID = 'web'

class Detector {
  // for ssr or debug, it can force specific ua string via _ua
  constructor({ appInfo, _ua } = {}) {
    const parser = new UAParser(_ua)

    const {
      browser,
      device,
      engine,
      os,
      ua
    } = parser.getResult()

    const {
      name,
      version,
    } = appInfo

    this.webName = name
    this.webVersion = version

    this.browser = browser
    this.device = device
    this.engine = engine
    this.os = os
    this.ua = ua

    this.platform = {
      app: /Flamingo/.test(this.ua),
      messenger: (this.os.name === 'iOS' && /FBAN\/Messenger/.test(this.ua))
        || (this.os.name === 'Android' && /Orca-Android/.test(this.ua)),
      instagram: /Instagram/.test(this.ua),
      facebook: /FBAN/.test(this.ua) || /FBAV/.test(this.ua)
    }
  }

  getUA() {
    return this.ua
  }

  isServer() {
    return isServer()
  }

  // common detector
  isIOS() {
    return this.os.name === 'iOS'
  }

  isAndroid() {
    return this.os.name === 'Android'
  }

  isMobile() {
    return this.device.type === 'mobile'
  }

  isTablet() {
    return this.device.type === 'tablet'
  }

  isDesktop() {
    return !this.isMobile() && !this.isApp()
  }

  isMessenger() {
    return this.platform.messenger
  }

  isInstagram() {
    return this.platform.instagram
  }

  isFacebook() {
    return this.platform.facebook
  }

  isApp() {
    // return true // TODO: VICTOR
    return this.platform.app
  }

  isPwa() {
    if (this.isServer()) {
      return false
    }

    return typeof window !== 'undefined' && (
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
    )
  }

  isBot() {
    const uaRegex = /bot|facebookexternalhit/
    return uaRegex.test(this.ua)
  }

  /* ------------------------------------ */

  // after app 5.0.0, release two key features
  //  1. switch h5 as native home page
  //  2. webview supports standalone flag, can fully hide the native navigator bar
  isLegacyApp() {
    if (!this.isApp()) {
      return false
    }

    // if app version version is lower than 5.0.0, then do not have h5Home
    if (this.isAndroid()) {
      return /Flamingo\/\d{5}/.test(this.getUA())
    }

    return /Flamingo\/[0-4]\./.test(this.getUA())
  }

  // app > v6.0.0
  // if app version version is greater than 6.0.0, they use app home again
  isUseAppHomeApp() {
    if (!this.isApp()) {
      return false
    }
    let isLegacy = /Flamingo\/[0-5]\./.test(this.getUA())

    return !isLegacy
  }

  // include app > v5.0.0 and h5HomeEnabled
  //   and pwa
  isStandaloneApp() {
    if (this.isPwa()) {
      return true
    }

    if (!this.isApp()) {
      return false
    }

    // actually, it will has a bug' even app is bigger than 5.0.0
    // , it can still switch to native home mode, so that this detector will give a wrong result
    // , for correct way is also check the ua, it should contains "FlamingoFeatures/h5HomeEnabled"
    return !this.isLegacyApp()
  }

  isWebStore() {
    return !this.isApp() && !this.isPwa()
  }

  /* ------------------------------------ */
  getAppVersion() {
    const matched = /Flamingo\/([\d.]+)/.exec(this.getUA())
    return matched ? matched[1] : 'N/A'
  }

  compareAppVersion({ targetVersion = '' }) {
    return compareVersion(this.getAppVersion(), targetVersion) >= 0
  }

  getWebVersion() {
    return this.webVersion
  }

  getClientVersion() {
    if (this.isApp()) {
      return `${this.getAppVersion()}:${this.getWebVersion()}`
    }

    return this.getWebVersion()
  }

  getClientId() {
    if (this.isApp()) {
      return this.isAndroid() ? ANDROID_CLIENT_ID : IOS_CLIENT_ID
    }
    return WEB_CLIENT_ID
  }

  getClientName() {
    return this.webName
  }
}

export default Detector
